import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import AXIOS from "../../../../config/api";
import { BASE_API } from "../../../../constants/apiUrls";
import { toast } from "../../../../lib/toast";
import {
  CommonOthers,
  CommonParams,
  ErrorMessage,
  GetCommonData,
  GetSingleDataParams,
  Params,
  PostRequest,
} from "../../../../types";
import {
  createDivisionData,
  divisionData,
} from "../../../../types/geo/geo";
import { handleApiError } from "../../../../utils/errorHandler";

// create division
export const createDivisionAsync = createAsyncThunk<
  null,
  PostRequest<createDivisionData, CommonOthers>
>(
  "admin-geo/createDivision",
  async ({ data, others: { reRender } }, { rejectWithValue }) => {
    try {
      await AXIOS.post(BASE_API.createDivision, data);
      reRender && reRender(true);
      toast("success", "Division created successfully");
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      handleApiError(error, "Division create failed");
      return rejectWithValue(error);
    }
  }
);

// get all division
export const getAllDivisionListAsync = createAsyncThunk<
  GetCommonData<divisionData>,
  Params<CommonParams & { divisionName?: string; custom_text?: string }>,
  {
    rejectValue: ErrorMessage;
  }
>("admin-geo/getAllDivisionList", async ({ params }, { rejectWithValue }) => {
  const { ...rest } = params || {};

  try {
    const data = await AXIOS.get(BASE_API.getDivision, {
      params: { ...rest },
    });

    return {
      data: data.data?.getData || [],
      length: data.data?.totalCount ?? (data.data?.getData?.length || 0),
    };
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    handleApiError(error, "Get division failed");
    return rejectWithValue(error);
  }
});

// get single division
export const getSingleDivisionAsync = createAsyncThunk<
  divisionData,
  Params<GetSingleDataParams>,
  {
    rejectValue: ErrorMessage;
  }
>("admin-geo/getSingleDivision", async ({ params }, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(`${BASE_API.getSingleDivision}/${params?.id}`);
    return data.data?.getData;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    handleApiError(error, "Get single division failed");
    return rejectWithValue(error);
  }
});

// update division
export const updateDivisionAsync = createAsyncThunk<
  null,
  PostRequest<createDivisionData, GetSingleDataParams>
>(
  "admin-geo/updateDivision",
  async ({ data, others: { reRender, id } }, { rejectWithValue }) => {
    try {
      await AXIOS.patch(`${BASE_API.updateDivision}/${id}`, data);
      reRender && reRender(true);
      toast("success", "Division updated successfully");
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      handleApiError(error, "Division update failed");
      return rejectWithValue(error);
    }
  }
);

// delete division
export const deleteDivisionAsync = createAsyncThunk<
  null,
  Params<GetSingleDataParams>,
  {
    rejectValue: ErrorMessage;
  }
>("admin-geo/deleteDivision", async ({ params }, { rejectWithValue }) => {
  try {
    await AXIOS.delete(`${BASE_API.deleteDivision}/${params?.id}`);
    params?.reRender && params?.reRender(true);
    toast("success", "Division deleted successfully");
    return null;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    handleApiError(error, "Division delete failed");
    return rejectWithValue(error);
  }
});

